/* eslint-disable @typescript-eslint/no-explicit-any */
import { all, put, select } from 'redux-saga/effects'
import { UseQueryOptions } from 'react-query'
import { MapStyle } from 'react-map-gl/maplibre'

import settings from 'settings'
import { fetchQueryEffect } from 'queryClient'

import { Basemap, BasemapConfig, Map } from 'features/maps/types'
import {
  setBasemapSettings,
  setCustomBaseMapSettings,
  setCustomBasemaps,
  setMapStyles,
  setMapStylesStatus,
  showGlobalBuilderError,
  updateBasemapConfig
} from './builderSlice'
import { RootState } from 'app/state/store'
import { accountBasemapsSettingsQuery, accountCustomBasemapsQuery } from 'features/account/api/accountQueries'
import { BasemapSettings } from 'features/account/types'
import { BuilderMode, BuilderBasemapStyle, MapStyleType } from '../types'

import { defaultBasemapsSettings } from '../ui/builderConstants'
import { getRequest } from 'features/common/api/requests'
import defaultMapStyles, {
  DEFAULT_MAP_STYLE_PLACEHOLDER,
  defaultVisibleLayerGroups
} from '../ui/KeplerGl/defaultMapStyles'
import { basemapFromKeplerMapStyle, basemapIdToStyleId, basemapToMapStyle } from '../model/basemap'
import { RequestStatus } from 'features/common'
import { updateCurrentMap } from 'features/maps/state/mapsSlice'
import { defaultMapStyleSelector } from '../model/mapStyles'

const DISABLED_QUERY = { enabled: false }

function addCurrentSelectedCustomBasemap(basemaps: Basemap[], currentSelectedCustomBasemap: Basemap | undefined) {
  if (!currentSelectedCustomBasemap) return basemaps

  const currentIdx = basemaps.findIndex((bm) => bm.id === currentSelectedCustomBasemap.id)
  if (currentIdx === -1) {
    return [{ ...currentSelectedCustomBasemap, disabled: true }, ...basemaps]
  } else {
    return basemaps
  }
}

type LoadedMapStyleObject = {
  id: string
  style?: unknown
  error?: boolean
}

function fetchMapStyleQuery(style: BuilderBasemapStyle): UseQueryOptions<LoadedMapStyleObject, Error> {
  const { id, url } = style
  if (!url) return DISABLED_QUERY

  return {
    queryKey: ['builderMapStyle', id],
    queryFn: async () => {
      try {
        let style = await getRequest(url, { anonymous: true })
        if (typeof style === 'string') {
          style = JSON.parse(style)
        }
        return {
          id,
          style
        }
      } catch (error) {
        console.error(`fetchMapStyleQuery: Couldn't load map style: ${url}`, error)
        return {
          id,
          error: true
        }
      }
    },
    staleTime: Infinity
  }
}

export function* loadBasemapConfigAndSettings(currentMap: Map) {
  try {
    const isBuilderPublicMode = yield select((state: RootState) => state.builder.builderMode === BuilderMode.Public)

    const basemapConfig = currentMap?.keplerMapConfig?.config?.basemapConfig
    if (basemapConfig) {
      yield put(updateBasemapConfig(basemapConfig))
    } else {
      const keplerMapStyle = currentMap?.keplerMapConfig?.config?.mapStyle
      const basemapConfig: BasemapConfig = {
        styleId: keplerMapStyle?.styleType || DEFAULT_MAP_STYLE_PLACEHOLDER,
        visibleLayerGroups: keplerMapStyle?.visibleLayerGroups || defaultVisibleLayerGroups
      }
      yield put(updateBasemapConfig(basemapConfig))
    }
    const currentSelectedCustomStyle = currentMap?.keplerMapConfig?.config?.customBaseMaps?.customStyle
    const currentSelectedCustomBasemap = currentSelectedCustomStyle
      ? basemapFromKeplerMapStyle(currentSelectedCustomStyle as BuilderBasemapStyle)
      : undefined

    let basemapSettings: BasemapSettings
    let customBasemaps: Basemap[] = []
    if (!isBuilderPublicMode) {
      basemapSettings = yield fetchQueryEffect(accountBasemapsSettingsQuery)

      if (basemapSettings?.custom || currentSelectedCustomBasemap) {
        customBasemaps = yield fetchQueryEffect(accountCustomBasemapsQuery)
      }
    } else {
      basemapSettings = currentMap?.basemaps?.settings || defaultBasemapsSettings
      customBasemaps = currentMap?.basemaps?.customBasemaps || []
    }
    if (!settings.googleMapsApiKey) {
      basemapSettings = { ...basemapSettings, google: false }
    }
    // api sometimes returns {custom: true} but no basemaps are added, disable them in this case
    if (customBasemaps.length === 0) {
      basemapSettings = { ...basemapSettings, custom: false }
    }
    customBasemaps = addCurrentSelectedCustomBasemap(customBasemaps, currentSelectedCustomBasemap)

    yield put(setBasemapSettings(basemapSettings))
    yield put(setCustomBasemaps(customBasemaps))
  } catch (error: any) {
    console.error('loadBasemapSettings failed', error)
    yield put(showGlobalBuilderError(error.message))
  }
}

function mapStyleTypeToSettingsKey(type: MapStyleType) {
  switch (type) {
    case MapStyleType.MAPLIBRE:
      return 'carto'
    case MapStyleType.GOOGLE_MAPS:
      return 'google'
    case MapStyleType.AMAZON:
      return 'amazon'
    case MapStyleType.CUSTOM:
      return 'custom'
  }
}

export function* loadMapStyles() {
  try {
    const basemapSettings: BasemapSettings = yield select((state: RootState) => state.builder.basemapSettings)
    const customBasemaps: Basemap[] = yield select((state: RootState) => state.builder.customBasemaps)
    const customMapStyles = customBasemaps ? customBasemaps.map(basemapToMapStyle) : []

    const stylesToLoad = [...defaultMapStyles, ...customMapStyles].map(
      (style): BuilderBasemapStyle => ({
        ...style,
        // this object is static and may contain functions, so we skip it here and reintroduce in mapStylesWithLayerGroupsSelector
        layerGroups: undefined,
        disabled: style.disabled || !basemapSettings?.[mapStyleTypeToSettingsKey(style.type)]
      })
    )

    const externalStyleFetchResults: LoadedMapStyleObject[] = yield all(
      stylesToLoad
        .filter((style) => style.url && !style.style)
        .map((style) => fetchQueryEffect(fetchMapStyleQuery(style)))
    )

    const loadedMapStyles = stylesToLoad.map((style): BuilderBasemapStyle => {
      if (style.style) {
        return style
      }

      const externalStyleResult = externalStyleFetchResults.find((result) => result.id === style.id)
      if (!externalStyleResult || externalStyleResult?.error) {
        return {
          ...style,
          style: {
            sources: {},
            layers: [],
            version: 8
          },
          failedToLoad: true,
          disabled: true
        }
      }

      return {
        ...style,
        style: {
          ...(externalStyleResult?.style as MapStyle)
        }
      }
    })
    yield put(setMapStyles(loadedMapStyles))
    yield put(setMapStylesStatus(RequestStatus.Fulfilled))
  } catch (error: any) {
    console.error('loadMapStyles failed', error)
    yield put(showGlobalBuilderError(error.message))
  }
}

export function* replaceAmazonBasemapWithNewDefault() {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)

    if (!currentMap) return
    const currentMapStyleId = currentMap?.keplerMapConfig?.config?.mapStyle?.styleType

    if (currentMapStyleId !== 'amazon') return

    const newDefaultStyle = yield select(defaultMapStyleSelector)
    let customStyle: BuilderBasemapStyle | undefined
    if (newDefaultStyle.type === MapStyleType.CUSTOM) {
      const customBasemaps: Basemap[] = yield select((state: RootState) => state.builder.customBasemaps)
      const currentBasemap = customBasemaps.find((bm) => basemapIdToStyleId(bm.id) === newDefaultStyle.id)
      customStyle = currentBasemap ? basemapToMapStyle(currentBasemap) : undefined
    }
    const newCurrentMap = {
      ...currentMap,
      keplerMapConfig: {
        ...currentMap.keplerMapConfig,
        config: {
          ...currentMap.keplerMapConfig?.config,
          mapStyle: {
            ...currentMap.keplerMapConfig?.config.mapStyle,
            styleType: newDefaultStyle.id
          },
          customBaseMaps: {
            ...currentMap.keplerMapConfig?.config.customBaseMaps,
            amazon: undefined,
            customStyle
          }
        }
      }
    }
    yield put(updateCurrentMap(newCurrentMap as Map))
  } catch (error: any) {
    console.error('replaceAmazonBasemapWithNewDefault failed', error)
    yield put(showGlobalBuilderError(error.message))
  }
}

export function* loadBasemapsSettings() {
  try {
    const currentMap: Map = yield select((state) => state.maps.currentMap)

    yield loadBasemapConfigAndSettings(currentMap)
    yield loadMapStyles()

    const customBaseMaps = currentMap?.keplerMapConfig?.config?.customBaseMaps

    if (customBaseMaps) {
      yield put(setCustomBaseMapSettings(customBaseMaps))
    }

    yield replaceAmazonBasemapWithNewDefault()
  } catch (e: any) {
    console.error('loadBasemapsSettings failed', e)
    yield put(showGlobalBuilderError(e.message))
  }
}
